import { Capacitor } from '@capacitor/core';
import { secureGet, secureSet, secureRemove } from './secureStorage';

const isNative = () => Capacitor.isNativePlatform();

/**
 * Check if biometric authentication is available on the device
 */
export async function checkBiometryAvailability() {
  if (!isNative()) {
    return { available: false, biometryType: null, reason: 'web' };
  }

  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    const result = await BiometricAuth.checkBiometry();
    return {
      available: result.isAvailable,
      biometryType: result.biometryType > 0 ? getBiometryName(result.biometryType) : null,
      reason: result.reason || null
    };
  } catch (err) {
    console.error('Biometry check error:', err);
    return { available: false, biometryType: null, reason: err.message };
  }
}

/**
 * Prompt the user for biometric authentication
 */
export async function promptBiometric(reason = 'Sign in to Finance Forecaster') {
  if (!isNative()) {
    return { success: false, error: 'NOT_NATIVE' };
  }

  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: 'Use Password',
      allowDeviceCredential: false
    });
    return { success: true, error: null };
  } catch (err) {
    if (err.message?.includes('cancel') || err.code === 'userCancel') {
      return { success: false, error: 'USER_CANCELLED' };
    }
    if (err.message?.includes('lockout') || err.code === 'biometryLockout') {
      return { success: false, error: 'LOCKOUT' };
    }
    return { success: false, error: err.message || 'UNKNOWN' };
  }
}

/**
 * Store a biometric token in secure storage
 */
export async function storeBiometricToken(token) {
  await secureSet('biometricToken', token);
  setBiometricEnrolledFlag(true);
}

/**
 * Retrieve the stored biometric token
 */
export async function retrieveBiometricToken() {
  return await secureGet('biometricToken');
}

/**
 * Clear stored biometric token and enrollment data
 */
export async function clearBiometricToken() {
  await secureRemove('biometricToken');
  await secureRemove('biometryType');
  setBiometricEnrolledFlag(false);
}

/**
 * Check if biometric is enrolled.
 * Uses a plain localStorage flag to avoid triggering a Face ID prompt
 * when reading from biometric-protected Keychain storage.
 */
export async function isBiometricEnrolled() {
  if (!isNative()) return false;
  return localStorage.getItem('biometricEnrolled') === 'true';
}

/**
 * Set the biometric enrollment flag (non-biometric-protected storage)
 */
export function setBiometricEnrolledFlag(enrolled) {
  if (enrolled) {
    localStorage.setItem('biometricEnrolled', 'true');
  } else {
    localStorage.removeItem('biometricEnrolled');
  }
}

/**
 * Get the stored biometry type name (Face ID, Touch ID, etc.)
 */
export async function getStoredBiometryType() {
  return await secureGet('biometryType');
}

/**
 * Store the biometry type for UI display
 */
export async function storeBiometryType(type) {
  await secureSet('biometryType', type);
}

/**
 * Get device ID for enrollment
 */
export async function getDeviceId() {
  if (!isNative()) return null;
  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getId();
    return info.identifier;
  } catch {
    return null;
  }
}

function getBiometryName(type) {
  // BiometryType enum from @aparajita/capacitor-biometric-auth
  switch (type) {
    case 1: return 'Touch ID';
    case 2: return 'Face ID';
    case 3: return 'Iris';
    case 4: return 'Fingerprint';
    default: return 'Biometric';
  }
}
